import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../../../shared/models/option.model';
import { ValueService } from '../../../../shared/value.service';
import { ProvinceService } from '../../../../services/data/province.service';
import { MunicipalityService } from '../../../../services/data/municipality.service';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Model180Service } from '../../model-180.service';
import {
  Model180Recipient,
  Model180Validation,
  normalizarPerceptor180,
  nuevoPerceptor180,
  ORIGEN_CONTABILIDAD,
  ORIGEN_MANUAL
} from '../taxes-180.model';

export interface Model180RecipientDialogData {
  row: Model180Recipient | null;
  ejercicio: number;
  validaciones: Model180Validation[];
}

const IMPORTES: string[] = ['base', 'porcentaje', 'retencion'];

function numero(valor: any): number {
  const convertido = Number(String(valor ?? '').replace(',', '.'));

  return isNaN(convertido) ? 0 : convertido;
}

@Component({
  selector: 'app-model-180-recipient-dialog',
  templateUrl: './model-180-recipient-dialog.component.html',
  styleUrls: ['./model-180-recipient-dialog.component.css'],
  standalone: false
})
export class Model180RecipientDialogComponent implements OnInit {

  f: any = null;
  saving = false;
  validaciones: Model180Validation[] = [];

  provinciaOptions: Option[] = [];
  tipoViaOptions: Option[] = [];
  municipioOptions: Option[] = [];

  readonly modalidadOptions: Option[] = [
    { label: 'Dineraria', value: '1' },
    { label: 'En especie', value: '2' }
  ];

  readonly sinoOptions: Option[] = [{ label: 'No', value: 'N' }, { label: 'Sí', value: 'S' }];

  readonly situacionOptions: Option[] = [
    { label: '1 - Territorio común, con referencia catastral', value: '1' },
    { label: '2 - País Vasco, con referencia catastral', value: '2' },
    { label: '3 - Navarra, con referencia catastral', value: '3' },
    { label: '4 - Sin referencia catastral', value: '4' }
  ];

  readonly tipoNumeracionOptions: Option[] = [
    { label: 'Número', value: 'NUM' },
    { label: 'Sin número', value: 'S/N' },
    { label: 'Kilómetro', value: 'KM.' },
    { label: 'Otro', value: 'OTR' }
  ];

  private municipios: { nombre: string, codigo: string }[] = [];

  static open(dialog: MatDialog, data: Model180RecipientDialogData): Observable<boolean> {
    return dialog.open<Model180RecipientDialogComponent, Model180RecipientDialogData, boolean>(
      Model180RecipientDialogComponent, {
      width: '1080px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data
    }).afterClosed().pipe(map(resultado => resultado === true));
  }

  constructor(
    private dialogRef: MatDialogRef<Model180RecipientDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: Model180RecipientDialogData,
    private service: Model180Service,
    private valueService: ValueService,
    private provinceService: ProvinceService,
    private municipalityService: MunicipalityService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    this.validaciones = this.data.validaciones || [];
    this.f = this.aFormulario(this.data.row ? normalizarPerceptor180(this.data.row) : nuevoPerceptor180());

    this.provinceService.getList().subscribe({
      next: respuesta => {
        this.provinciaOptions = (respuesta.data || [])
          .map((item: any) => ({ label: item.provinceName, value: item.provinceCode }));
      },
      error: () => { }
    });

    this.valueService.getList('STREET_TYPE')
      .then(respuesta => {
        const vistos = new Set<string>();
        const opciones: Option[] = [];

        (respuesta.data || []).forEach((item: any) => {
          const clave = String(item.key || '').trim();

          if (clave && !vistos.has(clave)) {
            vistos.add(clave);
            opciones.push({ label: item.data, value: clave });
          }
        });

        this.tipoViaOptions = opciones;
      })
      .catch(() => { });

    this.cargarMunicipios();
  }

  get titulo(): string {
    return this.data.row ? 'Editar perceptor' : 'Nuevo perceptor';
  }

  get subtitulo(): string {
    if (this.data.row && this.data.row.origen === ORIGEN_CONTABILIDAD) {
      return `Ejercicio ${this.data.ejercicio} · Calculado de la contabilidad: al guardar se sustituye por tu versión`;
    }

    return `Ejercicio ${this.data.ejercicio}`;
  }

  get municipiosSelect(): Option[] {
    const actual = this.f?.codigoMunicipio;

    if (actual && !this.municipioOptions.some(item => item.value === actual)) {
      return [{ label: this.f.municipio || actual, value: actual }, ...this.municipioOptions];
    }

    return this.municipioOptions;
  }

  trackByIndex(i: number) { return i; }

  private aFormulario(perceptor: Model180Recipient): any {
    const copia: any = { ...perceptor };

    IMPORTES.forEach(campo => copia[campo] = copia[campo] ? String(copia[campo]) : '');
    copia.ejercicioDevengo = copia.ejercicioDevengo ? String(copia.ejercicioDevengo) : '';
    copia.reintegro = copia.reintegro ? 'S' : 'N';

    return copia;
  }

  private aDto(): Model180Recipient {
    const copia: any = { ...this.f };

    IMPORTES.forEach(campo => copia[campo] = numero(copia[campo]));
    copia.ejercicioDevengo = Math.trunc(numero(copia.ejercicioDevengo));
    copia.reintegro = copia.reintegro === 'S';

    if (!this.data.row) {
      copia.uid = null;
      copia.clave = null;
      copia.origen = ORIGEN_MANUAL;
      copia.sobrescribeClave = null;
    } else if (this.data.row.origen === ORIGEN_CONTABILIDAD) {
      copia.uid = null;
      copia.clave = null;
      copia.origen = ORIGEN_MANUAL;
      copia.sobrescribeClave = this.data.row.clave;
    }

    return copia as Model180Recipient;
  }

  get(ruta: string): any {
    return this.f[ruta];
  }

  set(ruta: string, valor: any) {
    this.f[ruta] = valor;

    if (ruta === 'provinciaInmueble') {
      this.f.codigoMunicipio = '';
      this.f.municipio = '';
      this.cargarMunicipios();
    }
  }

  seleccionarMunicipio(codigo: string) {
    this.f.codigoMunicipio = codigo || '';

    const encontrado = this.municipios.find(item => item.codigo === codigo);

    if (encontrado) {
      this.f.municipio = encontrado.nombre;
    }
  }

  private cargarMunicipios() {
    const provincia = this.f?.provinciaInmueble;

    if (!provincia) {
      this.municipios = [];
      this.municipioOptions = [];
      return;
    }

    this.municipalityService.getList({ province_code: provincia }).subscribe({
      next: respuesta => {
        this.municipios = (respuesta.data || [])
          .map((item: any) => ({ nombre: item.municipalityName, codigo: item.municipalityCode }));
        this.municipioOptions = this.municipios.map(item => ({ label: item.nombre, value: item.codigo }));
      },
      error: () => {
        this.municipios = [];
        this.municipioOptions = [];
      }
    });
  }

  mensajes(): Model180Validation[] {
    return this.validaciones;
  }

  guardar() {
    if (this.saving) return;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const dto = this.aDto();
    const fila = this.data.row;
    const tarea = fila && fila.origen === ORIGEN_MANUAL && fila.uid
      ? this.service.updateRecipient(fila.uid, dto)
      : this.service.createRecipient(this.data.ejercicio, dto);

    tarea.then(() => {
      this.saving = false;
      this.dialogRef.disableClose = false;
      this.notification.ok('Perceptor guardado');
      this.dialogRef.close(true);
    }).catch(err => {
      this.saving = false;
      this.dialogRef.disableClose = false;
      this.notification.error(err?.error?.error?.messages?.[0] || 'No se ha podido guardar el perceptor');
    });
  }

  cerrar() {
    if (this.saving) return;

    this.dialogRef.close(false);
  }
}
